// src/hooks/useKycMockData.js
// Comprehensive mock data for KYC module development & testing.
// Used when the backend is not available (demo mode, dev, storybook).

import { useMemo } from 'react';

// ════════════════════════════════════════════════════════════════════════════
// ID helpers (deterministic UUIDs for reproducibility)
// ════════════════════════════════════════════════════════════════════════════

const id = (prefix, n) => `${prefix}-0000-0000-0000-${String(n).padStart(12, '0')}`;

const CASE_IDS = {
  horizon: id('case', 1),
  victoria: id('case', 2),
  sahara: id('case', 3),
  okonkwo: id('case', 4),
  globalImpact: id('case', 5),
  nakuru: id('case', 6),
  amara: id('case', 7),
  eastAfrica: id('case', 8),
};

const PERSON_IDS = {
  // Horizon Impact Partners
  horizonEntity: id('pers', 101),
  jamesMwangi: id('pers', 102),
  sarahOdhiambo: id('pers', 103),
  peterKariuki: id('pers', 104),
  // Victoria Chen
  victoriaChen: id('pers', 201),
  davidChen: id('pers', 202),
  // Sahara Growth Fund
  saharaEntity: id('pers', 301),
  abdulMalik: id('pers', 302),
  fatimaBintu: id('pers', 303),
  hassanOmar: id('pers', 304),
  laylaAmiri: id('pers', 305),
  // Okonkwo Family Office
  okonkwoEntity: id('pers', 401),
  chimaOkonkwo: id('pers', 402),
  ngoziFoOkonkwo: id('pers', 403),
  emekaNwosu: id('pers', 404),
  // Global Impact Trust
  trustEntity: id('pers', 501),
  margaritaSilva: id('pers', 502),
  johnMuturi: id('pers', 503),
  // Nakuru Investments
  nakuruEntity: id('pers', 601),
  aliNyong: id('pers', 602),
  euniceMuthoni: id('pers', 603),
  ibrahimSaid: id('pers', 604),
  // Amara Diallo
  amaraDiallo: id('pers', 701),
  // East Africa Ventures SPE
  speEntity: id('pers', 801),
  danielMutua: id('pers', 802),
  graceWanjiku: id('pers', 803),
};

const TENANT_ID = id('tenant', 1);
const ANALYST_ID = id('user', 10);
const MLRO_ID = id('user', 20);

// ════════════════════════════════════════════════════════════════════════════
// 1. CASES
// ════════════════════════════════════════════════════════════════════════════

export const mockCases = [
  {
    id: CASE_IDS.horizon,
    tenant_id: TENANT_ID,
    case_ref: 'KYC-2026-00001',
    case_type: 'corporate',
    status: 'approved',
    risk_rating: 'low',
    risk_score: 18,
    priority: 'normal',
    sla_hours: 72,
    due_date: '2026-06-15',
    notes: 'Kenyan corporate client, long-standing relationship. Clean screening results.',
    source_channel: 'portal',
    investor_tenant_id: null,
    assigned_analyst_id: ANALYST_ID,
    assigned_mlro_id: MLRO_ID,
    primary_person_id: PERSON_IDS.horizonEntity,
    submitted_at: '2026-05-20T09:00:00Z',
    approved_by: MLRO_ID,
    approved_at: '2026-06-10T14:30:00Z',
    created_by: ANALYST_ID,
    created_at: '2026-05-18T08:00:00Z',
    updated_at: '2026-06-10T14:30:00Z',
    tenant_name: 'Forwardsflow Securities Ltd',
  },
  {
    id: CASE_IDS.victoria,
    tenant_id: TENANT_ID,
    case_ref: 'KYC-2026-00002',
    case_type: 'individual',
    status: 'in_review',
    risk_rating: 'medium',
    risk_score: 45,
    priority: 'high',
    sla_hours: 48,
    due_date: '2026-09-18',
    notes: 'High-net-worth individual, Singaporean passport holder. PEP connection through spouse - former Singapore MAS board member.',
    source_channel: 'referral',
    investor_tenant_id: null,
    assigned_analyst_id: ANALYST_ID,
    assigned_mlro_id: MLRO_ID,
    primary_person_id: PERSON_IDS.victoriaChen,
    submitted_at: '2026-09-05T11:20:00Z',
    approved_by: null,
    approved_at: null,
    created_by: ANALYST_ID,
    created_at: '2026-09-01T06:00:00Z',
    updated_at: '2026-09-10T10:45:00Z',
    tenant_name: 'Forwardsflow Securities Ltd',
  },
  {
    id: CASE_IDS.sahara,
    tenant_id: TENANT_ID,
    case_ref: 'KYC-2026-00003',
    case_type: 'fund',
    status: 'screening',
    risk_rating: 'high',
    risk_score: 72,
    priority: 'urgent',
    sla_hours: 24,
    due_date: '2026-09-14',
    notes: 'North Africa growth fund. Adverse media hit on one director relating to tax evasion allegations in Morocco. Requires EDD.',
    source_channel: 'portal',
    investor_tenant_id: null,
    assigned_analyst_id: ANALYST_ID,
    assigned_mlro_id: MLRO_ID,
    primary_person_id: PERSON_IDS.saharaEntity,
    submitted_at: '2026-09-08T07:00:00Z',
    approved_by: null,
    approved_at: null,
    created_by: ANALYST_ID,
    created_at: '2026-09-06T10:00:00Z',
    updated_at: '2026-09-11T16:30:00Z',
    tenant_name: 'Forwardsflow Securities Ltd',
  },
  {
    id: CASE_IDS.okonkwo,
    tenant_id: TENANT_ID,
    case_ref: 'KYC-2026-00004',
    case_type: 'family_office',
    status: 'draft',
    risk_rating: null,
    risk_score: null,
    priority: 'normal',
    sla_hours: 72,
    due_date: '2026-09-25',
    notes: 'Nigerian family office, first-time onboarding. Documents being collected.',
    source_channel: 'portal',
    investor_tenant_id: null,
    assigned_analyst_id: ANALYST_ID,
    assigned_mlro_id: null,
    primary_person_id: PERSON_IDS.okonkwoEntity,
    submitted_at: null,
    approved_by: null,
    approved_at: null,
    created_by: ANALYST_ID,
    created_at: '2026-09-10T12:00:00Z',
    updated_at: '2026-09-10T12:00:00Z',
    tenant_name: 'Forwardsflow Securities Ltd',
  },
  {
    id: CASE_IDS.globalImpact,
    tenant_id: TENANT_ID,
    case_ref: 'KYC-2026-00005',
    case_type: 'trust',
    status: 'pending_approval',
    risk_rating: 'medium',
    risk_score: 38,
    priority: 'normal',
    sla_hours: 72,
    due_date: '2026-09-20',
    notes: 'Mauritius-based trust focused on East African impact investing. All docs verified, screening clear, awaiting MLRO sign-off.',
    source_channel: 'portal',
    investor_tenant_id: null,
    assigned_analyst_id: ANALYST_ID,
    assigned_mlro_id: MLRO_ID,
    primary_person_id: PERSON_IDS.trustEntity,
    submitted_at: '2026-08-25T09:00:00Z',
    approved_by: null,
    approved_at: null,
    created_by: ANALYST_ID,
    created_at: '2026-08-20T08:00:00Z',
    updated_at: '2026-09-08T11:00:00Z',
    tenant_name: 'Forwardsflow Securities Ltd',
  },
  {
    id: CASE_IDS.nakuru,
    tenant_id: TENANT_ID,
    case_ref: 'KYC-2026-00006',
    case_type: 'corporate',
    status: 'information_requested',
    risk_rating: 'high',
    risk_score: 65,
    priority: 'high',
    sla_hours: 48,
    due_date: '2026-09-16',
    notes: 'Kenya-registered company with Sudanese beneficial owner. Near-miss on OFAC sanctions list -- requires additional documentation and EDD. Waiting on updated source of funds evidence.',
    source_channel: 'portal',
    investor_tenant_id: null,
    assigned_analyst_id: ANALYST_ID,
    assigned_mlro_id: MLRO_ID,
    primary_person_id: PERSON_IDS.nakuruEntity,
    submitted_at: '2026-09-02T08:30:00Z',
    approved_by: null,
    approved_at: null,
    created_by: ANALYST_ID,
    created_at: '2026-08-28T10:00:00Z',
    updated_at: '2026-09-11T09:00:00Z',
    tenant_name: 'Forwardsflow Securities Ltd',
  },
  {
    id: CASE_IDS.amara,
    tenant_id: TENANT_ID,
    case_ref: 'KYC-2026-00007',
    case_type: 'individual',
    status: 'risk_assessment',
    risk_rating: 'medium',
    risk_score: 42,
    priority: 'normal',
    sla_hours: 72,
    due_date: '2026-09-22',
    notes: 'Senegalese national residing in Cote d\'Ivoire. Screening clear. Risk assessment in progress.',
    source_channel: 'referral',
    investor_tenant_id: null,
    assigned_analyst_id: ANALYST_ID,
    assigned_mlro_id: null,
    primary_person_id: PERSON_IDS.amaraDiallo,
    submitted_at: '2026-09-04T10:00:00Z',
    approved_by: null,
    approved_at: null,
    created_by: ANALYST_ID,
    created_at: '2026-09-02T07:00:00Z',
    updated_at: '2026-09-11T14:00:00Z',
    tenant_name: 'Forwardsflow Securities Ltd',
  },
  {
    id: CASE_IDS.eastAfrica,
    tenant_id: TENANT_ID,
    case_ref: 'KYC-2026-00008',
    case_type: 'spe',
    status: 'submitted',
    risk_rating: null,
    risk_score: null,
    priority: 'normal',
    sla_hours: 72,
    due_date: '2026-09-24',
    notes: 'Special purpose entity for East African venture investments. Just submitted, pending initial review.',
    source_channel: 'portal',
    investor_tenant_id: null,
    assigned_analyst_id: null,
    assigned_mlro_id: null,
    primary_person_id: PERSON_IDS.speEntity,
    submitted_at: '2026-09-11T06:00:00Z',
    approved_by: null,
    approved_at: null,
    created_by: ANALYST_ID,
    created_at: '2026-09-09T14:00:00Z',
    updated_at: '2026-09-11T06:00:00Z',
    tenant_name: 'Forwardsflow Securities Ltd',
  },
];

// ════════════════════════════════════════════════════════════════════════════
// 2. PERSONS
// ════════════════════════════════════════════════════════════════════════════

export const mockPersons = [
  // ── Horizon Impact Partners (KYC-2026-00001) ───────────────────────────
  {
    id: PERSON_IDS.horizonEntity,
    case_id: CASE_IDS.horizon,
    person_type: 'entity',
    role: 'applicant',
    is_primary: true,
    entity_name: 'Horizon Impact Partners Ltd',
    entity_type: 'private_limited',
    registration_number: 'PVT-2019-089234',
    registration_country: 'KE',
    incorporation_date: '2019-03-15',
    legal_form: 'Limited Company',
    email: 'compliance@horizonimpact.co.ke',
    phone: '+254720100200',
    address_line1: '14th Floor, Britam Tower',
    city: 'Nairobi',
    country: 'KE',
    postal_code: '00100',
    is_pep: false,
    is_sanctioned: false,
    verification_status: 'verified',
    created_at: '2026-05-18T08:10:00Z',
  },
  {
    id: PERSON_IDS.jamesMwangi,
    case_id: CASE_IDS.horizon,
    person_type: 'individual',
    role: 'director',
    is_primary: false,
    title: 'Mr',
    first_name: 'James',
    last_name: 'Mwangi',
    date_of_birth: '1975-08-22',
    nationality: 'KE',
    country_of_residence: 'KE',
    gender: 'male',
    email: 'james.mwangi@horizonimpact.co.ke',
    phone: '+254722100300',
    address_line1: '45 Riverside Drive',
    city: 'Nairobi',
    country: 'KE',
    is_pep: false,
    is_sanctioned: false,
    ownership_percentage: 45,
    voting_rights_percentage: 45,
    is_ubo: true,
    verification_status: 'verified',
    created_at: '2026-05-18T08:15:00Z',
  },
  {
    id: PERSON_IDS.sarahOdhiambo,
    case_id: CASE_IDS.horizon,
    person_type: 'individual',
    role: 'director',
    is_primary: false,
    title: 'Ms',
    first_name: 'Sarah',
    last_name: 'Odhiambo',
    date_of_birth: '1982-11-03',
    nationality: 'KE',
    country_of_residence: 'KE',
    gender: 'female',
    email: 'sarah.odhiambo@horizonimpact.co.ke',
    phone: '+254733200400',
    address_line1: '12 Kileleshwa Ring Road',
    city: 'Nairobi',
    country: 'KE',
    is_pep: false,
    is_sanctioned: false,
    ownership_percentage: 30,
    voting_rights_percentage: 30,
    is_ubo: true,
    verification_status: 'verified',
    created_at: '2026-05-18T08:20:00Z',
  },
  {
    id: PERSON_IDS.peterKariuki,
    case_id: CASE_IDS.horizon,
    person_type: 'individual',
    role: 'authorized_signatory',
    is_primary: false,
    title: 'Mr',
    first_name: 'Peter',
    last_name: 'Kariuki',
    date_of_birth: '1988-05-14',
    nationality: 'KE',
    country_of_residence: 'KE',
    gender: 'male',
    email: 'peter.kariuki@horizonimpact.co.ke',
    phone: '+254712300500',
    address_line1: '8 Lavington Green',
    city: 'Nairobi',
    country: 'KE',
    is_pep: false,
    is_sanctioned: false,
    ownership_percentage: 25,
    voting_rights_percentage: 25,
    is_ubo: true,
    verification_status: 'verified',
    created_at: '2026-05-18T08:25:00Z',
  },

  // ── Victoria Chen (KYC-2026-00002) ────────────────────────────────────
  {
    id: PERSON_IDS.victoriaChen,
    case_id: CASE_IDS.victoria,
    person_type: 'individual',
    role: 'applicant',
    is_primary: true,
    title: 'Ms',
    first_name: 'Victoria',
    last_name: 'Chen',
    date_of_birth: '1978-02-10',
    nationality: 'SG',
    dual_nationality: 'GB',
    country_of_residence: 'SG',
    gender: 'female',
    email: 'victoria.chen@privatewealth.sg',
    phone: '+6591234567',
    address_line1: '1 Nassim Road, #18-02',
    city: 'Singapore',
    country: 'SG',
    postal_code: '258458',
    is_pep: false,
    is_sanctioned: false,
    verification_status: 'verified',
    created_at: '2026-09-01T06:10:00Z',
  },
  {
    id: PERSON_IDS.davidChen,
    case_id: CASE_IDS.victoria,
    person_type: 'individual',
    role: 'connected_party',
    is_primary: false,
    title: 'Mr',
    first_name: 'David',
    last_name: 'Chen',
    date_of_birth: '1973-06-28',
    nationality: 'SG',
    country_of_residence: 'SG',
    gender: 'male',
    is_pep: true,
    pep_category: 'domestic_prominent',
    is_sanctioned: false,
    verification_status: 'verified',
    created_at: '2026-09-01T06:20:00Z',
  },

  // ── Sahara Growth Fund (KYC-2026-00003) ───────────────────────────────
  {
    id: PERSON_IDS.saharaEntity,
    case_id: CASE_IDS.sahara,
    person_type: 'entity',
    role: 'applicant',
    is_primary: true,
    entity_name: 'Sahara Growth Fund LP',
    entity_type: 'limited_partnership',
    registration_number: 'LP-2024-MA-45678',
    registration_country: 'MA',
    incorporation_date: '2024-01-20',
    legal_form: 'Limited Partnership',
    email: 'compliance@saharagrowth.ma',
    phone: '+212522001122',
    address_line1: 'Tour Atlas, Boulevard Zerktouni',
    city: 'Casablanca',
    country: 'MA',
    is_pep: false,
    is_sanctioned: false,
    verification_status: 'pending',
    created_at: '2026-09-06T10:10:00Z',
  },
  {
    id: PERSON_IDS.abdulMalik,
    case_id: CASE_IDS.sahara,
    person_type: 'individual',
    role: 'director',
    is_primary: false,
    title: 'Mr',
    first_name: 'Abdul',
    last_name: 'Malik',
    date_of_birth: '1968-09-05',
    nationality: 'MA',
    country_of_residence: 'MA',
    gender: 'male',
    email: 'a.malik@saharagrowth.ma',
    phone: '+212661223344',
    address_line1: '52 Avenue Hassan II',
    city: 'Rabat',
    country: 'MA',
    is_pep: false,
    is_sanctioned: false,
    ownership_percentage: 40,
    voting_rights_percentage: 40,
    is_ubo: true,
    verification_status: 'pending',
    created_at: '2026-09-06T10:20:00Z',
  },
  {
    id: PERSON_IDS.fatimaBintu,
    case_id: CASE_IDS.sahara,
    person_type: 'individual',
    role: 'beneficial_owner',
    is_primary: false,
    title: 'Ms',
    first_name: 'Fatima',
    last_name: 'Bintu',
    date_of_birth: '1980-04-17',
    nationality: 'NG',
    country_of_residence: 'MA',
    gender: 'female',
    email: 'f.bintu@saharagrowth.ma',
    phone: '+212662334455',
    address_line1: '18 Rue Moulay Youssef',
    city: 'Casablanca',
    country: 'MA',
    is_pep: false,
    is_sanctioned: false,
    ownership_percentage: 35,
    voting_rights_percentage: 35,
    is_ubo: true,
    verification_status: 'pending',
    created_at: '2026-09-06T10:25:00Z',
  },
  {
    id: PERSON_IDS.hassanOmar,
    case_id: CASE_IDS.sahara,
    person_type: 'individual',
    role: 'director',
    is_primary: false,
    title: 'Mr',
    first_name: 'Hassan',
    last_name: 'Omar',
    date_of_birth: '1972-12-01',
    nationality: 'TN',
    country_of_residence: 'TN',
    gender: 'male',
    email: 'h.omar@saharagrowth.ma',
    phone: '+21671445566',
    address_line1: '7 Avenue de la Republique',
    city: 'Tunis',
    country: 'TN',
    is_pep: false,
    is_sanctioned: false,
    ownership_percentage: 15,
    voting_rights_percentage: 15,
    is_ubo: false,
    verification_status: 'pending',
    created_at: '2026-09-06T10:30:00Z',
  },
  {
    id: PERSON_IDS.laylaAmiri,
    case_id: CASE_IDS.sahara,
    person_type: 'individual',
    role: 'authorized_signatory',
    is_primary: false,
    title: 'Ms',
    first_name: 'Layla',
    last_name: 'Amiri',
    date_of_birth: '1990-07-22',
    nationality: 'MA',
    country_of_residence: 'MA',
    gender: 'female',
    email: 'l.amiri@saharagrowth.ma',
    phone: '+212663445566',
    address_line1: '25 Rue de Fes',
    city: 'Casablanca',
    country: 'MA',
    is_pep: false,
    is_sanctioned: false,
    ownership_percentage: 10,
    voting_rights_percentage: 10,
    is_ubo: false,
    verification_status: 'pending',
    created_at: '2026-09-06T10:35:00Z',
  },

  // ── Okonkwo Family Office (KYC-2026-00004) ───────────────────────────
  {
    id: PERSON_IDS.okonkwoEntity,
    case_id: CASE_IDS.okonkwo,
    person_type: 'entity',
    role: 'applicant',
    is_primary: true,
    entity_name: 'Okonkwo Family Office Limited',
    entity_type: 'private_limited',
    registration_number: 'RC-2022-NG-112233',
    registration_country: 'NG',
    incorporation_date: '2022-06-01',
    legal_form: 'Limited Company',
    email: 'admin@okonkwofo.ng',
    phone: '+2348012345678',
    address_line1: '12 Victoria Island Boulevard',
    city: 'Lagos',
    country: 'NG',
    is_pep: false,
    is_sanctioned: false,
    verification_status: 'pending',
    created_at: '2026-09-10T12:05:00Z',
  },
  {
    id: PERSON_IDS.chimaOkonkwo,
    case_id: CASE_IDS.okonkwo,
    person_type: 'individual',
    role: 'director',
    is_primary: false,
    title: 'Chief',
    first_name: 'Chima',
    last_name: 'Okonkwo',
    date_of_birth: '1960-03-10',
    nationality: 'NG',
    country_of_residence: 'NG',
    gender: 'male',
    email: 'chima@okonkwofo.ng',
    phone: '+2348023456789',
    address_line1: '5 Banana Island Road',
    city: 'Lagos',
    country: 'NG',
    is_pep: true,
    pep_category: 'domestic_prominent',
    is_sanctioned: false,
    ownership_percentage: 60,
    voting_rights_percentage: 60,
    is_ubo: true,
    verification_status: 'pending',
    created_at: '2026-09-10T12:10:00Z',
  },
  {
    id: PERSON_IDS.ngoziFoOkonkwo,
    case_id: CASE_IDS.okonkwo,
    person_type: 'individual',
    role: 'beneficial_owner',
    is_primary: false,
    title: 'Mrs',
    first_name: 'Ngozi',
    last_name: 'Okonkwo',
    date_of_birth: '1965-11-20',
    nationality: 'NG',
    country_of_residence: 'NG',
    gender: 'female',
    email: 'ngozi@okonkwofo.ng',
    phone: '+2348034567890',
    address_line1: '5 Banana Island Road',
    city: 'Lagos',
    country: 'NG',
    is_pep: false,
    is_sanctioned: false,
    ownership_percentage: 30,
    voting_rights_percentage: 30,
    is_ubo: true,
    verification_status: 'pending',
    created_at: '2026-09-10T12:15:00Z',
  },
  {
    id: PERSON_IDS.emekaNwosu,
    case_id: CASE_IDS.okonkwo,
    person_type: 'individual',
    role: 'authorized_signatory',
    is_primary: false,
    title: 'Mr',
    first_name: 'Emeka',
    last_name: 'Nwosu',
    date_of_birth: '1985-01-15',
    nationality: 'NG',
    country_of_residence: 'NG',
    gender: 'male',
    email: 'emeka.nwosu@okonkwofo.ng',
    phone: '+2348045678901',
    address_line1: '22 Lekki Phase 1',
    city: 'Lagos',
    country: 'NG',
    is_pep: false,
    is_sanctioned: false,
    ownership_percentage: 10,
    voting_rights_percentage: 10,
    is_ubo: false,
    verification_status: 'pending',
    created_at: '2026-09-10T12:20:00Z',
  },

  // ── Global Impact Trust (KYC-2026-00005) ──────────────────────────────
  {
    id: PERSON_IDS.trustEntity,
    case_id: CASE_IDS.globalImpact,
    person_type: 'entity',
    role: 'applicant',
    is_primary: true,
    entity_name: 'Global Impact Trust',
    entity_type: 'trust',
    registration_number: 'TR-2021-MU-77890',
    registration_country: 'MU',
    incorporation_date: '2021-09-01',
    legal_form: 'Unit Trust',
    email: 'trustee@globalimpact.mu',
    phone: '+23052301234',
    address_line1: 'Level 3, Nexteracom Tower',
    city: 'Port Louis',
    country: 'MU',
    is_pep: false,
    is_sanctioned: false,
    verification_status: 'verified',
    created_at: '2026-08-20T08:10:00Z',
  },
  {
    id: PERSON_IDS.margaritaSilva,
    case_id: CASE_IDS.globalImpact,
    person_type: 'individual',
    role: 'director',
    is_primary: false,
    title: 'Dr',
    first_name: 'Margarita',
    last_name: 'Silva',
    date_of_birth: '1970-04-30',
    nationality: 'MU',
    dual_nationality: 'ZA',
    country_of_residence: 'MU',
    gender: 'female',
    email: 'margarita.silva@globalimpact.mu',
    phone: '+23052405678',
    address_line1: '15 Royal Road, Floreal',
    city: 'Curepipe',
    country: 'MU',
    is_pep: false,
    is_sanctioned: false,
    ownership_percentage: 0,
    voting_rights_percentage: 100,
    is_ubo: false,
    verification_status: 'verified',
    created_at: '2026-08-20T08:20:00Z',
  },
  {
    id: PERSON_IDS.johnMuturi,
    case_id: CASE_IDS.globalImpact,
    person_type: 'individual',
    role: 'beneficial_owner',
    is_primary: false,
    title: 'Mr',
    first_name: 'John',
    last_name: 'Muturi',
    date_of_birth: '1966-08-12',
    nationality: 'KE',
    country_of_residence: 'KE',
    gender: 'male',
    email: 'john.muturi@gmail.com',
    phone: '+254721506789',
    address_line1: '88 State House Road',
    city: 'Nairobi',
    country: 'KE',
    is_pep: false,
    is_sanctioned: false,
    ownership_percentage: 100,
    voting_rights_percentage: 0,
    is_ubo: true,
    verification_status: 'verified',
    created_at: '2026-08-20T08:25:00Z',
  },

  // ── Nakuru Investments Ltd (KYC-2026-00006) ───────────────────────────
  {
    id: PERSON_IDS.nakuruEntity,
    case_id: CASE_IDS.nakuru,
    person_type: 'entity',
    role: 'applicant',
    is_primary: true,
    entity_name: 'Nakuru Investments Ltd',
    entity_type: 'private_limited',
    registration_number: 'PVT-2023-KE-55678',
    registration_country: 'KE',
    incorporation_date: '2023-02-10',
    legal_form: 'Limited Company',
    email: 'info@nakuruinvestments.co.ke',
    phone: '+254720556677',
    address_line1: '3rd Floor, Lake View Towers',
    city: 'Nakuru',
    country: 'KE',
    postal_code: '20100',
    is_pep: false,
    is_sanctioned: false,
    verification_status: 'verified',
    created_at: '2026-08-28T10:10:00Z',
  },
  {
    id: PERSON_IDS.aliNyong,
    case_id: CASE_IDS.nakuru,
    person_type: 'individual',
    role: 'director',
    is_primary: false,
    title: 'Mr',
    first_name: 'Ali',
    last_name: 'Nyong\'o',
    date_of_birth: '1971-06-18',
    nationality: 'KE',
    country_of_residence: 'KE',
    gender: 'male',
    email: 'ali.nyongo@nakuruinvestments.co.ke',
    phone: '+254723667788',
    address_line1: '14 Oginga Odinga Road',
    city: 'Nakuru',
    country: 'KE',
    is_pep: false,
    is_sanctioned: false,
    ownership_percentage: 40,
    voting_rights_percentage: 40,
    is_ubo: true,
    verification_status: 'verified',
    created_at: '2026-08-28T10:15:00Z',
  },
  {
    id: PERSON_IDS.euniceMuthoni,
    case_id: CASE_IDS.nakuru,
    person_type: 'individual',
    role: 'director',
    is_primary: false,
    title: 'Ms',
    first_name: 'Eunice',
    last_name: 'Muthoni',
    date_of_birth: '1985-01-25',
    nationality: 'KE',
    country_of_residence: 'KE',
    gender: 'female',
    email: 'eunice.muthoni@nakuruinvestments.co.ke',
    phone: '+254734778899',
    address_line1: '21 Kenyatta Avenue',
    city: 'Nakuru',
    country: 'KE',
    is_pep: false,
    is_sanctioned: false,
    ownership_percentage: 25,
    voting_rights_percentage: 25,
    is_ubo: true,
    verification_status: 'verified',
    created_at: '2026-08-28T10:20:00Z',
  },
  {
    id: PERSON_IDS.ibrahimSaid,
    case_id: CASE_IDS.nakuru,
    person_type: 'individual',
    role: 'beneficial_owner',
    is_primary: false,
    title: 'Mr',
    first_name: 'Ibrahim',
    last_name: 'Said',
    date_of_birth: '1964-09-08',
    nationality: 'SD',
    country_of_residence: 'KE',
    gender: 'male',
    email: 'ibrahim.said@gmail.com',
    phone: '+254725889900',
    address_line1: '6 Milimani Estate',
    city: 'Nakuru',
    country: 'KE',
    is_pep: false,
    is_sanctioned: false,
    ownership_percentage: 35,
    voting_rights_percentage: 35,
    is_ubo: true,
    verification_status: 'pending',
    created_at: '2026-08-28T10:25:00Z',
  },

  // ── Amara Diallo (KYC-2026-00007) ────────────────────────────────────
  {
    id: PERSON_IDS.amaraDiallo,
    case_id: CASE_IDS.amara,
    person_type: 'individual',
    role: 'applicant',
    is_primary: true,
    title: 'Ms',
    first_name: 'Amara',
    last_name: 'Diallo',
    date_of_birth: '1983-03-19',
    nationality: 'SN',
    country_of_residence: 'CI',
    gender: 'female',
    email: 'amara.diallo@investci.com',
    phone: '+2250504112233',
    address_line1: '22 Boulevard de la Paix, Cocody',
    city: 'Abidjan',
    country: 'CI',
    postal_code: '01 BP 5678',
    is_pep: false,
    is_sanctioned: false,
    verification_status: 'verified',
    created_at: '2026-09-02T07:10:00Z',
  },

  // ── East Africa Ventures SPE (KYC-2026-00008) ────────────────────────
  {
    id: PERSON_IDS.speEntity,
    case_id: CASE_IDS.eastAfrica,
    person_type: 'entity',
    role: 'applicant',
    is_primary: true,
    entity_name: 'East Africa Ventures SPE Ltd',
    entity_type: 'special_purpose_entity',
    registration_number: 'SPE-2026-KE-90123',
    registration_country: 'KE',
    incorporation_date: '2026-07-15',
    legal_form: 'Special Purpose Entity',
    email: 'ops@eaventures.co.ke',
    phone: '+254720990011',
    address_line1: 'Upper Hill, Ngong Road',
    city: 'Nairobi',
    country: 'KE',
    is_pep: false,
    is_sanctioned: false,
    verification_status: 'pending',
    created_at: '2026-09-09T14:10:00Z',
  },
  {
    id: PERSON_IDS.danielMutua,
    case_id: CASE_IDS.eastAfrica,
    person_type: 'individual',
    role: 'director',
    is_primary: false,
    title: 'Mr',
    first_name: 'Daniel',
    last_name: 'Mutua',
    date_of_birth: '1979-10-05',
    nationality: 'KE',
    country_of_residence: 'KE',
    gender: 'male',
    email: 'daniel.mutua@eaventures.co.ke',
    phone: '+254711001122',
    address_line1: '3 Kilimani Road',
    city: 'Nairobi',
    country: 'KE',
    is_pep: false,
    is_sanctioned: false,
    ownership_percentage: 50,
    voting_rights_percentage: 50,
    is_ubo: true,
    verification_status: 'pending',
    created_at: '2026-09-09T14:15:00Z',
  },
  {
    id: PERSON_IDS.graceWanjiku,
    case_id: CASE_IDS.eastAfrica,
    person_type: 'individual',
    role: 'director',
    is_primary: false,
    title: 'Ms',
    first_name: 'Grace',
    last_name: 'Wanjiku',
    date_of_birth: '1986-07-30',
    nationality: 'KE',
    country_of_residence: 'TZ',
    gender: 'female',
    email: 'grace.wanjiku@eaventures.co.ke',
    phone: '+255754223344',
    address_line1: '18 Ali Hassan Mwinyi Road',
    city: 'Dar es Salaam',
    country: 'TZ',
    is_pep: false,
    is_sanctioned: false,
    ownership_percentage: 50,
    voting_rights_percentage: 50,
    is_ubo: true,
    verification_status: 'pending',
    created_at: '2026-09-09T14:20:00Z',
  },
];

// ════════════════════════════════════════════════════════════════════════════
// 3. DOCUMENTS
// ════════════════════════════════════════════════════════════════════════════

export const mockDocuments = [
  // ── Horizon: all verified ─────────────────────────────────────────────
  {
    id: id('doc', 1001),
    person_id: PERSON_IDS.horizonEntity,
    case_id: CASE_IDS.horizon,
    doc_type: 'certificate_of_incorporation',
    document_number: 'COI-KE-2019-089234',
    issuing_country: 'KE',
    issuing_authority: 'Kenya Registrar of Companies',
    issue_date: '2019-03-15',
    expiry_date: null,
    verification_status: 'verified',
    verified_by: ANALYST_ID,
    verified_at: '2026-05-22T10:00:00Z',
    first_name: null,
    last_name: null,
    entity_name: 'Horizon Impact Partners Ltd',
    created_at: '2026-05-18T09:00:00Z',
  },
  {
    id: id('doc', 1002),
    person_id: PERSON_IDS.horizonEntity,
    case_id: CASE_IDS.horizon,
    doc_type: 'cr12_register_of_directors',
    document_number: 'CR12-2026-001',
    issuing_country: 'KE',
    issuing_authority: 'Kenya Registrar of Companies',
    issue_date: '2026-04-01',
    expiry_date: '2027-04-01',
    verification_status: 'verified',
    verified_by: ANALYST_ID,
    verified_at: '2026-05-22T10:05:00Z',
    first_name: null,
    last_name: null,
    entity_name: 'Horizon Impact Partners Ltd',
    created_at: '2026-05-18T09:05:00Z',
  },
  {
    id: id('doc', 1003),
    person_id: PERSON_IDS.jamesMwangi,
    case_id: CASE_IDS.horizon,
    doc_type: 'national_id',
    document_number: '12345678',
    issuing_country: 'KE',
    issuing_authority: 'Kenya National Registration Bureau',
    issue_date: '2020-06-15',
    expiry_date: null,
    verification_status: 'verified',
    verified_by: ANALYST_ID,
    verified_at: '2026-05-22T10:10:00Z',
    first_name: 'James',
    last_name: 'Mwangi',
    entity_name: null,
    created_at: '2026-05-18T09:10:00Z',
  },
  {
    id: id('doc', 1004),
    person_id: PERSON_IDS.jamesMwangi,
    case_id: CASE_IDS.horizon,
    doc_type: 'kra_pin',
    document_number: 'A001234567B',
    issuing_country: 'KE',
    issuing_authority: 'Kenya Revenue Authority',
    issue_date: '2018-01-10',
    expiry_date: null,
    verification_status: 'verified',
    verified_by: ANALYST_ID,
    verified_at: '2026-05-22T10:15:00Z',
    first_name: 'James',
    last_name: 'Mwangi',
    entity_name: null,
    created_at: '2026-05-18T09:15:00Z',
  },

  // ── Victoria Chen: verified passport, pending utility bill ───────────
  {
    id: id('doc', 2001),
    person_id: PERSON_IDS.victoriaChen,
    case_id: CASE_IDS.victoria,
    doc_type: 'passport',
    document_number: 'E12345678A',
    issuing_country: 'SG',
    issuing_authority: 'Immigration & Checkpoints Authority',
    issue_date: '2022-03-20',
    expiry_date: '2032-03-19',
    verification_status: 'verified',
    verified_by: ANALYST_ID,
    verified_at: '2026-09-06T14:00:00Z',
    first_name: 'Victoria',
    last_name: 'Chen',
    entity_name: null,
    created_at: '2026-09-01T07:00:00Z',
  },
  {
    id: id('doc', 2002),
    person_id: PERSON_IDS.victoriaChen,
    case_id: CASE_IDS.victoria,
    doc_type: 'proof_of_address',
    document_number: null,
    issuing_country: 'SG',
    issuing_authority: null,
    issue_date: '2026-08-01',
    expiry_date: '2026-11-01',
    verification_status: 'pending',
    verified_by: null,
    verified_at: null,
    first_name: 'Victoria',
    last_name: 'Chen',
    entity_name: null,
    created_at: '2026-09-05T11:30:00Z',
  },
  {
    id: id('doc', 2003),
    person_id: PERSON_IDS.victoriaChen,
    case_id: CASE_IDS.victoria,
    doc_type: 'source_of_wealth_evidence',
    document_number: null,
    issuing_country: 'SG',
    issuing_authority: 'DBS Bank',
    issue_date: '2026-07-15',
    expiry_date: null,
    verification_status: 'verified',
    verified_by: ANALYST_ID,
    verified_at: '2026-09-07T09:00:00Z',
    first_name: 'Victoria',
    last_name: 'Chen',
    entity_name: null,
    created_at: '2026-09-03T08:00:00Z',
  },

  // ── Sahara: mostly pending ───────────────────────────────────────────
  {
    id: id('doc', 3001),
    person_id: PERSON_IDS.saharaEntity,
    case_id: CASE_IDS.sahara,
    doc_type: 'certificate_of_incorporation',
    document_number: 'LP-2024-MA-45678',
    issuing_country: 'MA',
    issuing_authority: 'OMPIC Morocco',
    issue_date: '2024-01-20',
    expiry_date: null,
    verification_status: 'pending',
    verified_by: null,
    verified_at: null,
    first_name: null,
    last_name: null,
    entity_name: 'Sahara Growth Fund LP',
    created_at: '2026-09-06T11:00:00Z',
  },
  {
    id: id('doc', 3002),
    person_id: PERSON_IDS.abdulMalik,
    case_id: CASE_IDS.sahara,
    doc_type: 'passport',
    document_number: 'MA9876543',
    issuing_country: 'MA',
    issuing_authority: 'Ministry of Interior',
    issue_date: '2021-05-10',
    expiry_date: '2031-05-09',
    verification_status: 'pending',
    verified_by: null,
    verified_at: null,
    first_name: 'Abdul',
    last_name: 'Malik',
    entity_name: null,
    created_at: '2026-09-06T11:10:00Z',
  },

  // ── Nakuru: one expired, triggering alert ─────────────────────────────
  {
    id: id('doc', 6001),
    person_id: PERSON_IDS.nakuruEntity,
    case_id: CASE_IDS.nakuru,
    doc_type: 'certificate_of_incorporation',
    document_number: 'PVT-2023-KE-55678',
    issuing_country: 'KE',
    issuing_authority: 'Kenya Registrar of Companies',
    issue_date: '2023-02-10',
    expiry_date: null,
    verification_status: 'verified',
    verified_by: ANALYST_ID,
    verified_at: '2026-09-03T10:00:00Z',
    first_name: null,
    last_name: null,
    entity_name: 'Nakuru Investments Ltd',
    created_at: '2026-08-28T11:00:00Z',
  },
  {
    id: id('doc', 6002),
    person_id: PERSON_IDS.ibrahimSaid,
    case_id: CASE_IDS.nakuru,
    doc_type: 'passport',
    document_number: 'SD-P-554433',
    issuing_country: 'SD',
    issuing_authority: 'Civil Registry',
    issue_date: '2016-08-01',
    expiry_date: '2026-07-31',
    verification_status: 'expired',
    verified_by: ANALYST_ID,
    verified_at: '2026-09-03T10:30:00Z',
    first_name: 'Ibrahim',
    last_name: 'Said',
    entity_name: null,
    created_at: '2026-08-28T11:10:00Z',
  },
  {
    id: id('doc', 6003),
    person_id: PERSON_IDS.ibrahimSaid,
    case_id: CASE_IDS.nakuru,
    doc_type: 'proof_of_address',
    document_number: null,
    issuing_country: 'KE',
    issuing_authority: null,
    issue_date: '2026-06-15',
    expiry_date: '2026-09-15',
    verification_status: 'rejected',
    verified_by: ANALYST_ID,
    verified_at: '2026-09-03T11:00:00Z',
    rejection_reason: 'Document older than 3 months; address does not match declaration.',
    first_name: 'Ibrahim',
    last_name: 'Said',
    entity_name: null,
    created_at: '2026-08-28T11:15:00Z',
  },

  // ── Amara Diallo: verified ───────────────────────────────────────────
  {
    id: id('doc', 7001),
    person_id: PERSON_IDS.amaraDiallo,
    case_id: CASE_IDS.amara,
    doc_type: 'passport',
    document_number: 'SN-2024-778899',
    issuing_country: 'SN',
    issuing_authority: 'Direction de la Police Nationale',
    issue_date: '2024-01-05',
    expiry_date: '2034-01-04',
    verification_status: 'verified',
    verified_by: ANALYST_ID,
    verified_at: '2026-09-05T10:00:00Z',
    first_name: 'Amara',
    last_name: 'Diallo',
    entity_name: null,
    created_at: '2026-09-02T08:00:00Z',
  },
  {
    id: id('doc', 7002),
    person_id: PERSON_IDS.amaraDiallo,
    case_id: CASE_IDS.amara,
    doc_type: 'proof_of_address',
    document_number: null,
    issuing_country: 'CI',
    issuing_authority: 'CIE (Compagnie Ivoirienne d\'Electricite)',
    issue_date: '2026-08-10',
    expiry_date: '2026-11-10',
    verification_status: 'verified',
    verified_by: ANALYST_ID,
    verified_at: '2026-09-05T10:15:00Z',
    first_name: 'Amara',
    last_name: 'Diallo',
    entity_name: null,
    created_at: '2026-09-02T08:05:00Z',
  },
];

// ════════════════════════════════════════════════════════════════════════════
// 4. SCREENING RESULTS
// ════════════════════════════════════════════════════════════════════════════

export const mockScreeningResults = [
  // ── Horizon: all clear ────────────────────────────────────────────────
  {
    id: id('scr', 1001),
    case_id: CASE_IDS.horizon,
    person_id: PERSON_IDS.jamesMwangi,
    screening_type: 'pep,sanctions,adverse_media',
    vendor: 'refinitiv_world_check',
    vendor_ref: 'WC-2026-001234',
    overall_status: 'clear',
    total_hits: 0,
    reviewed_hits: 0,
    screened_at: '2026-05-25T09:00:00Z',
    next_screening_due: '2026-08-25',
    hits: [],
  },
  {
    id: id('scr', 1002),
    case_id: CASE_IDS.horizon,
    person_id: PERSON_IDS.sarahOdhiambo,
    screening_type: 'pep,sanctions,adverse_media',
    vendor: 'refinitiv_world_check',
    vendor_ref: 'WC-2026-001235',
    overall_status: 'clear',
    total_hits: 0,
    reviewed_hits: 0,
    screened_at: '2026-05-25T09:05:00Z',
    next_screening_due: '2026-08-25',
    hits: [],
  },

  // ── Victoria Chen: PEP hit on spouse ──────────────────────────────────
  {
    id: id('scr', 2001),
    case_id: CASE_IDS.victoria,
    person_id: PERSON_IDS.victoriaChen,
    screening_type: 'pep,sanctions,adverse_media',
    vendor: 'refinitiv_world_check',
    vendor_ref: 'WC-2026-002001',
    overall_status: 'clear',
    total_hits: 0,
    reviewed_hits: 0,
    screened_at: '2026-09-06T10:00:00Z',
    next_screening_due: '2026-12-06',
    hits: [],
  },
  {
    id: id('scr', 2002),
    case_id: CASE_IDS.victoria,
    person_id: PERSON_IDS.davidChen,
    screening_type: 'pep,sanctions',
    vendor: 'refinitiv_world_check',
    vendor_ref: 'WC-2026-002002',
    overall_status: 'pending_review',
    total_hits: 1,
    reviewed_hits: 0,
    screened_at: '2026-09-06T10:05:00Z',
    next_screening_due: '2026-12-06',
    hits: [
      {
        id: id('hit', 2001),
        screening_result_id: id('scr', 2002),
        hit_type: 'pep',
        list_name: 'World-Check PEP Database',
        matched_name: 'David Chen Wei Liang',
        match_score: 92,
        status: 'pending_review',
        matched_data: {
          position: 'Former Board Member, Monetary Authority of Singapore',
          category: 'Government / Regulatory Body',
          date_of_birth: '1973-06-28',
          nationality: 'Singaporean',
          notes: 'Board member 2015-2023. Now private sector.',
        },
        resolved_by: null,
        resolved_at: null,
        resolution_notes: null,
        created_at: '2026-09-06T10:05:30Z',
      },
    ],
  },

  // ── Sahara: adverse media hit ─────────────────────────────────────────
  {
    id: id('scr', 3001),
    case_id: CASE_IDS.sahara,
    person_id: PERSON_IDS.abdulMalik,
    screening_type: 'pep,sanctions,adverse_media',
    vendor: 'dow_jones_risk_compliance',
    vendor_ref: 'DJ-2026-003001',
    overall_status: 'pending_review',
    total_hits: 1,
    reviewed_hits: 0,
    screened_at: '2026-09-09T14:00:00Z',
    next_screening_due: '2026-12-09',
    hits: [
      {
        id: id('hit', 3001),
        screening_result_id: id('scr', 3001),
        hit_type: 'adverse_media',
        list_name: 'Dow Jones Adverse Media',
        matched_name: 'Abdul Malik Al-Fassi',
        match_score: 78,
        status: 'pending_review',
        matched_data: {
          headline: 'Moroccan businessman under investigation for tax irregularities',
          source: 'L\'Economiste (Morocco)',
          date: '2025-11-15',
          summary: 'Moroccan tax authority investigating real estate holding company for underreporting rental income. No charges filed.',
          severity: 'medium',
        },
        resolved_by: null,
        resolved_at: null,
        resolution_notes: null,
        created_at: '2026-09-09T14:00:30Z',
      },
    ],
  },

  // ── Nakuru: sanctions near-miss ───────────────────────────────────────
  {
    id: id('scr', 6001),
    case_id: CASE_IDS.nakuru,
    person_id: PERSON_IDS.ibrahimSaid,
    screening_type: 'pep,sanctions,adverse_media',
    vendor: 'refinitiv_world_check',
    vendor_ref: 'WC-2026-006001',
    overall_status: 'pending_review',
    total_hits: 1,
    reviewed_hits: 0,
    screened_at: '2026-09-04T11:00:00Z',
    next_screening_due: '2026-12-04',
    hits: [
      {
        id: id('hit', 6001),
        screening_result_id: id('scr', 6001),
        hit_type: 'sanctions',
        list_name: 'OFAC SDN List',
        matched_name: 'Ibrahim Said Mohamed',
        match_score: 68,
        status: 'escalated',
        matched_data: {
          list: 'OFAC Specially Designated Nationals',
          program: 'SUDAN',
          date_of_birth: '1965-01-01',
          nationality: 'Sudanese',
          notes: 'Near-miss. Subject DOB differs by ~4 months. Name is common in Sudan. Requires additional verification.',
          aliases: ['Ibrahim Said Muhammad', 'Ibrahim S. Mohamed'],
        },
        resolved_by: null,
        resolved_at: null,
        resolution_notes: null,
        created_at: '2026-09-04T11:00:30Z',
      },
    ],
  },

  // ── Amara Diallo: clear ──────────────────────────────────────────────
  {
    id: id('scr', 7001),
    case_id: CASE_IDS.amara,
    person_id: PERSON_IDS.amaraDiallo,
    screening_type: 'pep,sanctions,adverse_media',
    vendor: 'refinitiv_world_check',
    vendor_ref: 'WC-2026-007001',
    overall_status: 'clear',
    total_hits: 0,
    reviewed_hits: 0,
    screened_at: '2026-09-07T08:00:00Z',
    next_screening_due: '2026-12-07',
    hits: [],
  },
];

// ════════════════════════════════════════════════════════════════════════════
// 5. RISK ASSESSMENTS
// ════════════════════════════════════════════════════════════════════════════

export const mockRiskAssessments = [
  {
    id: id('risk', 1001),
    case_id: CASE_IDS.horizon,
    assessment_type: 'initial',
    overall_risk: 'low',
    overall_score: 18,
    customer_risk_score: 15,
    country_risk_score: 20,
    product_risk_score: 18,
    channel_risk_score: 20,
    methodology_version: 'v1.0',
    assessed_by: ANALYST_ID,
    is_override: false,
    override_reason: null,
    next_review_date: '2027-06-10',
    notes: 'Standard onboarding, all clear.',
    created_at: '2026-06-05T10:00:00Z',
    factors: [
      { id: id('rf', 1), assessment_id: id('risk', 1001), category: 'customer', factor_name: 'Entity type', factor_value: 'Kenyan limited company', weight: 1.0, score: 2, max_score: 10, notes: null },
      { id: id('rf', 2), assessment_id: id('risk', 1001), category: 'customer', factor_name: 'Years in operation', factor_value: '7 years', weight: 1.0, score: 1, max_score: 10, notes: 'Long track record' },
      { id: id('rf', 3), assessment_id: id('risk', 1001), category: 'country', factor_name: 'Country of incorporation', factor_value: 'Kenya', weight: 1.5, score: 3, max_score: 10, notes: 'Medium FATF rating' },
      { id: id('rf', 4), assessment_id: id('risk', 1001), category: 'product', factor_name: 'Product complexity', factor_value: 'Standard equities', weight: 1.0, score: 2, max_score: 10, notes: null },
      { id: id('rf', 5), assessment_id: id('risk', 1001), category: 'channel', factor_name: 'Onboarding channel', factor_value: 'Face-to-face', weight: 1.0, score: 1, max_score: 10, notes: 'Direct relationship' },
    ],
  },
  {
    id: id('risk', 2001),
    case_id: CASE_IDS.victoria,
    assessment_type: 'initial',
    overall_risk: 'medium',
    overall_score: 45,
    customer_risk_score: 50,
    country_risk_score: 25,
    product_risk_score: 40,
    channel_risk_score: 55,
    methodology_version: 'v1.0',
    assessed_by: ANALYST_ID,
    is_override: false,
    override_reason: null,
    next_review_date: '2027-03-10',
    notes: 'Elevated due to PEP connection. Spouse is former MAS board member.',
    created_at: '2026-09-08T09:00:00Z',
    factors: [
      { id: id('rf', 6), assessment_id: id('risk', 2001), category: 'customer', factor_name: 'PEP relationship', factor_value: 'Spouse is domestic PEP', weight: 2.0, score: 7, max_score: 10, notes: 'Former MAS board member' },
      { id: id('rf', 7), assessment_id: id('risk', 2001), category: 'customer', factor_name: 'Net worth bracket', factor_value: 'USD 5M-50M', weight: 1.0, score: 4, max_score: 10, notes: null },
      { id: id('rf', 8), assessment_id: id('risk', 2001), category: 'country', factor_name: 'Country of residence', factor_value: 'Singapore', weight: 1.0, score: 2, max_score: 10, notes: 'Low-risk jurisdiction' },
      { id: id('rf', 9), assessment_id: id('risk', 2001), category: 'product', factor_name: 'Investment scope', factor_value: 'Multi-market fixed income', weight: 1.0, score: 5, max_score: 10, notes: null },
      { id: id('rf', 10), assessment_id: id('risk', 2001), category: 'channel', factor_name: 'Referral source', factor_value: 'Third-party introducer', weight: 1.5, score: 6, max_score: 10, notes: 'Non face-to-face' },
    ],
  },
  {
    id: id('risk', 3001),
    case_id: CASE_IDS.sahara,
    assessment_type: 'initial',
    overall_risk: 'high',
    overall_score: 72,
    customer_risk_score: 70,
    country_risk_score: 75,
    product_risk_score: 65,
    channel_risk_score: 70,
    methodology_version: 'v1.0',
    assessed_by: ANALYST_ID,
    is_override: false,
    override_reason: null,
    next_review_date: '2026-12-09',
    notes: 'High risk due to adverse media on director and fund structure crossing multiple North African jurisdictions.',
    created_at: '2026-09-10T14:00:00Z',
    factors: [
      { id: id('rf', 11), assessment_id: id('risk', 3001), category: 'customer', factor_name: 'Entity structure', factor_value: 'Multi-jurisdiction LP', weight: 2.0, score: 8, max_score: 10, notes: 'Complex fund structure' },
      { id: id('rf', 12), assessment_id: id('risk', 3001), category: 'customer', factor_name: 'Adverse media', factor_value: 'Tax evasion allegations', weight: 2.5, score: 7, max_score: 10, notes: 'Under investigation, no charges' },
      { id: id('rf', 13), assessment_id: id('risk', 3001), category: 'country', factor_name: 'Operating jurisdictions', factor_value: 'Morocco, Tunisia, Nigeria', weight: 1.5, score: 7, max_score: 10, notes: 'Mixed FATF ratings' },
      { id: id('rf', 14), assessment_id: id('risk', 3001), category: 'product', factor_name: 'Investment strategy', factor_value: 'Growth equity, illiquid', weight: 1.0, score: 6, max_score: 10, notes: null },
    ],
  },
  {
    id: id('risk', 5001),
    case_id: CASE_IDS.globalImpact,
    assessment_type: 'initial',
    overall_risk: 'medium',
    overall_score: 38,
    customer_risk_score: 35,
    country_risk_score: 40,
    product_risk_score: 35,
    channel_risk_score: 40,
    methodology_version: 'v1.0',
    assessed_by: ANALYST_ID,
    is_override: false,
    override_reason: null,
    next_review_date: '2027-09-08',
    notes: 'Mauritius trust is standard structure but cross-border element with Kenya UBO adds moderate risk.',
    created_at: '2026-09-05T10:00:00Z',
    factors: [
      { id: id('rf', 15), assessment_id: id('risk', 5001), category: 'customer', factor_name: 'Entity type', factor_value: 'Unit trust (Mauritius)', weight: 1.0, score: 4, max_score: 10, notes: null },
      { id: id('rf', 16), assessment_id: id('risk', 5001), category: 'country', factor_name: 'Country of registration', factor_value: 'Mauritius', weight: 1.0, score: 4, max_score: 10, notes: 'FATF grey-list considerations' },
      { id: id('rf', 17), assessment_id: id('risk', 5001), category: 'product', factor_name: 'Impact investing', factor_value: 'ESG-aligned', weight: 0.8, score: 3, max_score: 10, notes: 'Lower risk profile' },
    ],
  },
  {
    id: id('risk', 6001),
    case_id: CASE_IDS.nakuru,
    assessment_type: 'initial',
    overall_risk: 'high',
    overall_score: 65,
    customer_risk_score: 70,
    country_risk_score: 80,
    product_risk_score: 45,
    channel_risk_score: 55,
    methodology_version: 'v1.0',
    assessed_by: ANALYST_ID,
    is_override: false,
    override_reason: null,
    next_review_date: '2026-12-11',
    notes: 'High country risk from Sudanese UBO. Near-miss sanctions match requires EDD.',
    created_at: '2026-09-06T15:00:00Z',
    factors: [
      { id: id('rf', 18), assessment_id: id('risk', 6001), category: 'customer', factor_name: 'UBO nationality', factor_value: 'Sudan', weight: 3.0, score: 9, max_score: 10, notes: 'High-risk jurisdiction, OFAC programme' },
      { id: id('rf', 19), assessment_id: id('risk', 6001), category: 'customer', factor_name: 'Sanctions near-miss', factor_value: '68% match score', weight: 2.5, score: 7, max_score: 10, notes: 'Requires positive confirmation of non-match' },
      { id: id('rf', 20), assessment_id: id('risk', 6001), category: 'country', factor_name: 'UBO country of origin', factor_value: 'Sudan', weight: 2.0, score: 9, max_score: 10, notes: 'Comprehensive US sanctions' },
    ],
  },
  {
    id: id('risk', 7001),
    case_id: CASE_IDS.amara,
    assessment_type: 'initial',
    overall_risk: 'medium',
    overall_score: 42,
    customer_risk_score: 40,
    country_risk_score: 50,
    product_risk_score: 35,
    channel_risk_score: 40,
    methodology_version: 'v1.0',
    assessed_by: ANALYST_ID,
    is_override: false,
    override_reason: null,
    next_review_date: '2027-09-11',
    notes: 'Medium risk due to cross-border element (Senegalese national in Cote d\'Ivoire). Screening clear.',
    created_at: '2026-09-10T08:00:00Z',
    factors: [
      { id: id('rf', 21), assessment_id: id('risk', 7001), category: 'customer', factor_name: 'Cross-border residency', factor_value: 'Senegal national in CI', weight: 1.5, score: 5, max_score: 10, notes: null },
      { id: id('rf', 22), assessment_id: id('risk', 7001), category: 'country', factor_name: 'Country of residence', factor_value: 'Cote d\'Ivoire', weight: 1.0, score: 5, max_score: 10, notes: 'GIABA member, moderate AML framework' },
    ],
  },
];

// ════════════════════════════════════════════════════════════════════════════
// 6. MARKET ONBOARDINGS
// ════════════════════════════════════════════════════════════════════════════

export const mockMarketOnboardings = [
  // ── Horizon: Kenya fully onboarded ────────────────────────────────────
  {
    id: id('mo', 1001),
    case_id: CASE_IDS.horizon,
    market_code: 'KE',
    rule_set_id: id('mrs', 1),
    status: 'completed',
    total_requirements: 6,
    completed_requirements: 6,
    market_name: 'Kenya',
    regulator: 'Capital Markets Authority (CMA)',
    csd_name: 'CDSC Kenya',
    requirements: [
      { id: id('mr', 1), case_id: CASE_IDS.horizon, market_code: 'KE', requirement_type: 'document', requirement_key: 'certificate_of_incorporation', requirement_label: 'Upload certificate of incorporation', is_mandatory: true, status: 'completed', fulfilled_by: ANALYST_ID, fulfilled_at: '2026-05-22T10:00:00Z', sort_order: 0 },
      { id: id('mr', 2), case_id: CASE_IDS.horizon, market_code: 'KE', requirement_type: 'document', requirement_key: 'cr12_register_of_directors', requirement_label: 'Upload CR12 register of directors', is_mandatory: true, status: 'completed', fulfilled_by: ANALYST_ID, fulfilled_at: '2026-05-22T10:05:00Z', sort_order: 1 },
      { id: id('mr', 3), case_id: CASE_IDS.horizon, market_code: 'KE', requirement_type: 'screening', requirement_key: 'screening_complete', requirement_label: 'Complete screening checks', is_mandatory: true, status: 'completed', fulfilled_by: ANALYST_ID, fulfilled_at: '2026-05-25T09:10:00Z', sort_order: 2 },
      { id: id('mr', 4), case_id: CASE_IDS.horizon, market_code: 'KE', requirement_type: 'risk_assessment', requirement_key: 'risk_assessment_complete', requirement_label: 'Complete risk assessment', is_mandatory: true, status: 'completed', fulfilled_by: ANALYST_ID, fulfilled_at: '2026-06-05T10:05:00Z', sort_order: 3 },
      { id: id('mr', 5), case_id: CASE_IDS.horizon, market_code: 'KE', requirement_type: 'csd_account', requirement_key: 'csd_account_registered', requirement_label: 'Register CSD account with CDSC Kenya', is_mandatory: true, status: 'completed', fulfilled_by: ANALYST_ID, fulfilled_at: '2026-06-07T15:00:00Z', sort_order: 4 },
      { id: id('mr', 6), case_id: CASE_IDS.horizon, market_code: 'KE', requirement_type: 'approval', requirement_key: 'final_approval', requirement_label: 'Final approval', is_mandatory: true, status: 'completed', fulfilled_by: MLRO_ID, fulfilled_at: '2026-06-10T14:30:00Z', sort_order: 5 },
    ],
  },

  // ── Victoria Chen: Kenya partially done, Uganda not started ──────────
  {
    id: id('mo', 2001),
    case_id: CASE_IDS.victoria,
    market_code: 'KE',
    rule_set_id: id('mrs', 1),
    status: 'in_progress',
    total_requirements: 5,
    completed_requirements: 2,
    market_name: 'Kenya',
    regulator: 'Capital Markets Authority (CMA)',
    csd_name: 'CDSC Kenya',
    requirements: [
      { id: id('mr', 20), case_id: CASE_IDS.victoria, market_code: 'KE', requirement_type: 'document', requirement_key: 'passport', requirement_label: 'Upload passport', is_mandatory: true, status: 'completed', fulfilled_by: ANALYST_ID, fulfilled_at: '2026-09-06T14:00:00Z', sort_order: 0 },
      { id: id('mr', 21), case_id: CASE_IDS.victoria, market_code: 'KE', requirement_type: 'document', requirement_key: 'proof_of_address', requirement_label: 'Upload proof of address', is_mandatory: true, status: 'pending', fulfilled_by: null, fulfilled_at: null, sort_order: 1 },
      { id: id('mr', 22), case_id: CASE_IDS.victoria, market_code: 'KE', requirement_type: 'screening', requirement_key: 'screening_complete', requirement_label: 'Complete screening checks', is_mandatory: true, status: 'completed', fulfilled_by: ANALYST_ID, fulfilled_at: '2026-09-06T10:10:00Z', sort_order: 2 },
      { id: id('mr', 23), case_id: CASE_IDS.victoria, market_code: 'KE', requirement_type: 'risk_assessment', requirement_key: 'risk_assessment_complete', requirement_label: 'Complete risk assessment', is_mandatory: true, status: 'pending', fulfilled_by: null, fulfilled_at: null, sort_order: 3 },
      { id: id('mr', 24), case_id: CASE_IDS.victoria, market_code: 'KE', requirement_type: 'approval', requirement_key: 'final_approval', requirement_label: 'Final approval', is_mandatory: true, status: 'pending', fulfilled_by: null, fulfilled_at: null, sort_order: 4 },
    ],
  },
  {
    id: id('mo', 2002),
    case_id: CASE_IDS.victoria,
    market_code: 'UG',
    rule_set_id: id('mrs', 2),
    status: 'not_started',
    total_requirements: 5,
    completed_requirements: 0,
    market_name: 'Uganda',
    regulator: 'Capital Markets Authority (Uganda)',
    csd_name: 'SCD Uganda',
    requirements: [
      { id: id('mr', 25), case_id: CASE_IDS.victoria, market_code: 'UG', requirement_type: 'document', requirement_key: 'passport', requirement_label: 'Upload passport', is_mandatory: true, status: 'pending', fulfilled_by: null, fulfilled_at: null, sort_order: 0 },
      { id: id('mr', 26), case_id: CASE_IDS.victoria, market_code: 'UG', requirement_type: 'screening', requirement_key: 'screening_complete', requirement_label: 'Complete screening checks', is_mandatory: true, status: 'pending', fulfilled_by: null, fulfilled_at: null, sort_order: 1 },
      { id: id('mr', 27), case_id: CASE_IDS.victoria, market_code: 'UG', requirement_type: 'risk_assessment', requirement_key: 'risk_assessment_complete', requirement_label: 'Complete risk assessment', is_mandatory: true, status: 'pending', fulfilled_by: null, fulfilled_at: null, sort_order: 2 },
      { id: id('mr', 28), case_id: CASE_IDS.victoria, market_code: 'UG', requirement_type: 'csd_account', requirement_key: 'csd_account_registered', requirement_label: 'Register CSD account with SCD Uganda', is_mandatory: true, status: 'pending', fulfilled_by: null, fulfilled_at: null, sort_order: 3 },
      { id: id('mr', 29), case_id: CASE_IDS.victoria, market_code: 'UG', requirement_type: 'approval', requirement_key: 'final_approval', requirement_label: 'Final approval', is_mandatory: true, status: 'pending', fulfilled_by: null, fulfilled_at: null, sort_order: 4 },
    ],
  },

  // ── Global Impact Trust: Kenya & Tanzania ─────────────────────────────
  {
    id: id('mo', 5001),
    case_id: CASE_IDS.globalImpact,
    market_code: 'KE',
    rule_set_id: id('mrs', 1),
    status: 'in_progress',
    total_requirements: 5,
    completed_requirements: 4,
    market_name: 'Kenya',
    regulator: 'Capital Markets Authority (CMA)',
    csd_name: 'CDSC Kenya',
    requirements: [
      { id: id('mr', 50), case_id: CASE_IDS.globalImpact, market_code: 'KE', requirement_type: 'document', requirement_key: 'trust_deed', requirement_label: 'Upload trust deed', is_mandatory: true, status: 'completed', fulfilled_by: ANALYST_ID, fulfilled_at: '2026-08-28T10:00:00Z', sort_order: 0 },
      { id: id('mr', 51), case_id: CASE_IDS.globalImpact, market_code: 'KE', requirement_type: 'screening', requirement_key: 'screening_complete', requirement_label: 'Complete screening checks', is_mandatory: true, status: 'completed', fulfilled_by: ANALYST_ID, fulfilled_at: '2026-09-01T09:00:00Z', sort_order: 1 },
      { id: id('mr', 52), case_id: CASE_IDS.globalImpact, market_code: 'KE', requirement_type: 'risk_assessment', requirement_key: 'risk_assessment_complete', requirement_label: 'Complete risk assessment', is_mandatory: true, status: 'completed', fulfilled_by: ANALYST_ID, fulfilled_at: '2026-09-05T10:00:00Z', sort_order: 2 },
      { id: id('mr', 53), case_id: CASE_IDS.globalImpact, market_code: 'KE', requirement_type: 'csd_account', requirement_key: 'csd_account_registered', requirement_label: 'Register CSD account with CDSC Kenya', is_mandatory: true, status: 'completed', fulfilled_by: ANALYST_ID, fulfilled_at: '2026-09-06T15:00:00Z', sort_order: 3 },
      { id: id('mr', 54), case_id: CASE_IDS.globalImpact, market_code: 'KE', requirement_type: 'approval', requirement_key: 'final_approval', requirement_label: 'Final approval', is_mandatory: true, status: 'pending', fulfilled_by: null, fulfilled_at: null, sort_order: 4 },
    ],
  },
  {
    id: id('mo', 5002),
    case_id: CASE_IDS.globalImpact,
    market_code: 'TZ',
    rule_set_id: id('mrs', 3),
    status: 'in_progress',
    total_requirements: 5,
    completed_requirements: 2,
    market_name: 'Tanzania',
    regulator: 'Capital Markets and Securities Authority (CMSA)',
    csd_name: 'CSD & Registry Company (CSDR)',
    requirements: [
      { id: id('mr', 55), case_id: CASE_IDS.globalImpact, market_code: 'TZ', requirement_type: 'document', requirement_key: 'trust_deed', requirement_label: 'Upload trust deed', is_mandatory: true, status: 'completed', fulfilled_by: ANALYST_ID, fulfilled_at: '2026-08-28T10:05:00Z', sort_order: 0 },
      { id: id('mr', 56), case_id: CASE_IDS.globalImpact, market_code: 'TZ', requirement_type: 'screening', requirement_key: 'screening_complete', requirement_label: 'Complete screening checks', is_mandatory: true, status: 'completed', fulfilled_by: ANALYST_ID, fulfilled_at: '2026-09-01T09:05:00Z', sort_order: 1 },
      { id: id('mr', 57), case_id: CASE_IDS.globalImpact, market_code: 'TZ', requirement_type: 'risk_assessment', requirement_key: 'risk_assessment_complete', requirement_label: 'Complete risk assessment', is_mandatory: true, status: 'pending', fulfilled_by: null, fulfilled_at: null, sort_order: 2 },
      { id: id('mr', 58), case_id: CASE_IDS.globalImpact, market_code: 'TZ', requirement_type: 'csd_account', requirement_key: 'csd_account_registered', requirement_label: 'Register CSD account with CSDR', is_mandatory: true, status: 'pending', fulfilled_by: null, fulfilled_at: null, sort_order: 3 },
      { id: id('mr', 59), case_id: CASE_IDS.globalImpact, market_code: 'TZ', requirement_type: 'approval', requirement_key: 'final_approval', requirement_label: 'Final approval', is_mandatory: true, status: 'pending', fulfilled_by: null, fulfilled_at: null, sort_order: 4 },
    ],
  },
];

// ════════════════════════════════════════════════════════════════════════════
// 7. MONITORING ALERTS
// ════════════════════════════════════════════════════════════════════════════

export const mockAlerts = [
  {
    id: id('alert', 1),
    case_id: CASE_IDS.nakuru,
    alert_type: 'document_expiry',
    severity: 'high',
    status: 'open',
    title: 'Expired passport - Ibrahim Said',
    description: 'Passport SD-P-554433 for Ibrahim Said expired on 2026-07-31. Replacement required to maintain compliance.',
    assigned_to: ANALYST_ID,
    resolved_by: null,
    resolved_at: null,
    resolution_notes: null,
    created_at: '2026-08-01T00:00:00Z',
  },
  {
    id: id('alert', 2),
    case_id: CASE_IDS.victoria,
    alert_type: 'screening_update',
    severity: 'medium',
    status: 'open',
    title: 'Periodic screening due - David Chen',
    description: 'Quarterly PEP/sanctions rescreening due for David Chen (connected party). Last screened 2026-09-06.',
    assigned_to: ANALYST_ID,
    resolved_by: null,
    resolved_at: null,
    resolution_notes: null,
    created_at: '2026-09-11T08:00:00Z',
  },
  {
    id: id('alert', 3),
    case_id: CASE_IDS.horizon,
    alert_type: 'review_due',
    severity: 'low',
    status: 'open',
    title: 'Annual review approaching - Horizon Impact Partners',
    description: 'Annual KYC review due by 2027-06-10. Case is low risk with no changes since approval.',
    assigned_to: ANALYST_ID,
    resolved_by: null,
    resolved_at: null,
    resolution_notes: null,
    created_at: '2026-09-10T06:00:00Z',
  },
  {
    id: id('alert', 4),
    case_id: CASE_IDS.sahara,
    alert_type: 'adverse_media',
    severity: 'high',
    status: 'open',
    title: 'New adverse media - Abdul Malik',
    description: 'New article published regarding ongoing tax investigation in Morocco. Requires review and possible EDD refresh.',
    assigned_to: ANALYST_ID,
    resolved_by: null,
    resolved_at: null,
    resolution_notes: null,
    created_at: '2026-09-12T05:00:00Z',
  },
];

// ════════════════════════════════════════════════════════════════════════════
// 8. REVIEWS
// ════════════════════════════════════════════════════════════════════════════

export const mockReviews = [
  {
    id: id('rev', 1),
    case_id: CASE_IDS.horizon,
    review_type: 'periodic',
    frequency_months: 12,
    next_review_date: '2027-06-10',
    status: 'scheduled',
    outcome: null,
    outcome_notes: null,
    last_review_date: '2026-06-10',
    last_reviewed_by: MLRO_ID,
    case_ref: 'KYC-2026-00001',
    risk_rating: 'low',
    created_at: '2026-06-10T15:00:00Z',
  },
  {
    id: id('rev', 2),
    case_id: CASE_IDS.nakuru,
    review_type: 'event_triggered',
    frequency_months: 6,
    next_review_date: '2026-09-15',
    status: 'scheduled',
    outcome: null,
    outcome_notes: null,
    trigger_reason: 'Expired passport and sanctions near-miss require accelerated review',
    last_review_date: null,
    last_reviewed_by: null,
    case_ref: 'KYC-2026-00006',
    risk_rating: 'high',
    created_at: '2026-09-05T10:00:00Z',
  },
  {
    id: id('rev', 3),
    case_id: CASE_IDS.sahara,
    review_type: 'edd',
    frequency_months: 3,
    next_review_date: '2026-12-09',
    status: 'scheduled',
    outcome: null,
    outcome_notes: null,
    trigger_reason: 'EDD required due to adverse media hit',
    last_review_date: null,
    last_reviewed_by: null,
    case_ref: 'KYC-2026-00003',
    risk_rating: 'high',
    created_at: '2026-09-10T15:00:00Z',
  },
];

// ════════════════════════════════════════════════════════════════════════════
// 9. TASKS
// ════════════════════════════════════════════════════════════════════════════

export const mockTasks = [
  {
    id: id('task', 1),
    case_id: CASE_IDS.victoria,
    task_type: 'document_collection',
    title: 'Collect updated proof of address from Victoria Chen',
    description: 'Current utility bill or bank statement dated within last 3 months for Singapore address.',
    status: 'pending',
    priority: 'high',
    assigned_to: ANALYST_ID,
    assigned_role: 'bank_compliance_maker',
    due_date: '2026-09-15',
    case_ref: 'KYC-2026-00002',
    created_at: '2026-09-10T09:00:00Z',
  },
  {
    id: id('task', 2),
    case_id: CASE_IDS.nakuru,
    task_type: 'edd',
    title: 'Complete EDD on Ibrahim Said - sanctions near-miss',
    description: 'Obtain additional documentation to positively confirm the OFAC SDN near-miss is a false positive. Collect birth certificate, additional ID, and employer letter.',
    status: 'in_progress',
    priority: 'urgent',
    assigned_to: ANALYST_ID,
    assigned_role: 'bank_compliance_maker',
    due_date: '2026-09-14',
    case_ref: 'KYC-2026-00006',
    created_at: '2026-09-05T10:00:00Z',
  },
  {
    id: id('task', 3),
    case_id: CASE_IDS.sahara,
    task_type: 'screening_review',
    title: 'Review adverse media hit - Abdul Malik tax investigation',
    description: 'Assess the materiality of tax evasion allegations in Morocco. Determine if this triggers EDD or case rejection.',
    status: 'pending',
    priority: 'high',
    assigned_to: MLRO_ID,
    assigned_role: 'bank_compliance_maker',
    due_date: '2026-09-15',
    case_ref: 'KYC-2026-00003',
    created_at: '2026-09-10T14:30:00Z',
  },
  {
    id: id('task', 4),
    case_id: CASE_IDS.globalImpact,
    task_type: 'approval',
    title: 'MLRO approval - Global Impact Trust',
    description: 'All requirements for Kenya market onboarding complete. Risk assessment completed. Awaiting MLRO final sign-off.',
    status: 'pending',
    priority: 'normal',
    assigned_to: MLRO_ID,
    assigned_role: 'bank_compliance_maker',
    due_date: '2026-09-18',
    case_ref: 'KYC-2026-00005',
    created_at: '2026-09-08T11:00:00Z',
  },
  {
    id: id('task', 5),
    case_id: CASE_IDS.eastAfrica,
    task_type: 'initial_review',
    title: 'Assign analyst and begin initial review - East Africa Ventures SPE',
    description: 'New SPE submission received. Assign analyst, verify entity structure, and begin document collection.',
    status: 'pending',
    priority: 'normal',
    assigned_to: null,
    assigned_role: 'bank_compliance_maker',
    due_date: '2026-09-16',
    case_ref: 'KYC-2026-00008',
    created_at: '2026-09-11T06:05:00Z',
  },
];

// ════════════════════════════════════════════════════════════════════════════
// 10. STATS
// ════════════════════════════════════════════════════════════════════════════

export const mockStats = {
  totalCases: 8,
  byStatus: {
    draft: 1,
    submitted: 1,
    in_review: 1,
    screening: 1,
    risk_assessment: 1,
    pending_approval: 1,
    approved: 1,
    information_requested: 1,
  },
  byRisk: {
    low: 2,
    medium: 3,
    high: 2,
    unscored: 1,
  },
  overdueSla: 1,
  pendingScreeningHits: 3,
  upcomingReviews: 2,
  pendingApproval: 1,
  last30Days: 5,
};

// ════════════════════════════════════════════════════════════════════════════
// HOOK
// ════════════════════════════════════════════════════════════════════════════

/**
 * Hook that returns all mock KYC data in a structured object.
 * Used when the backend is not available (demo mode, development).
 */
export const useKycMockData = () => {
  const data = useMemo(
    () => ({
      cases: mockCases,
      persons: mockPersons,
      documents: mockDocuments,
      screeningResults: mockScreeningResults,
      riskAssessments: mockRiskAssessments,
      marketOnboardings: mockMarketOnboardings,
      alerts: mockAlerts,
      reviews: mockReviews,
      tasks: mockTasks,
      stats: mockStats,

      // ── Convenience getters ───────────────────────────────────────────

      /** Get persons for a specific case */
      getPersonsForCase: (caseId) => mockPersons.filter((p) => p.case_id === caseId),

      /** Get documents for a specific case */
      getDocumentsForCase: (caseId) => mockDocuments.filter((d) => d.case_id === caseId),

      /** Get screening results for a specific case */
      getScreeningForCase: (caseId) => mockScreeningResults.filter((s) => s.case_id === caseId),

      /** Get risk assessments for a specific case */
      getRiskForCase: (caseId) => mockRiskAssessments.filter((r) => r.case_id === caseId),

      /** Get market onboardings for a specific case */
      getMarketsForCase: (caseId) => mockMarketOnboardings.filter((m) => m.case_id === caseId),

      /** Get alerts for a specific case */
      getAlertsForCase: (caseId) => mockAlerts.filter((a) => a.case_id === caseId),

      /** Get tasks for a specific case */
      getTasksForCase: (caseId) => mockTasks.filter((t) => t.case_id === caseId),

      /** Get a single case by ID */
      getCaseById: (caseId) => mockCases.find((c) => c.id === caseId) || null,

      /** Get all pending screening hits across all cases */
      getPendingHits: () =>
        mockScreeningResults.reduce((acc, r) => {
          const pending = (r.hits || []).filter(
            (h) => h.status === 'pending_review' || h.status === 'escalated'
          );
          return [
            ...acc,
            ...pending.map((h) => ({
              ...h,
              case_id: r.case_id,
              person_id: r.person_id,
              screening_type: r.screening_type,
              vendor: r.vendor,
              case_ref: mockCases.find((c) => c.id === r.case_id)?.case_ref,
            })),
          ];
        }, []),
    }),
    []
  );

  return data;
};

export default useKycMockData;
